import { useEffect, useState } from 'react'

export interface StageSizeOptions {
  /** Fraction of `window.innerWidth` the stage uses (e.g. 0.875 = 87.5%). */
  widthFactor: number
  /** Pixels subtracted from `window.innerHeight` (header/modal chrome). */
  heightOffset: number
}

export interface StageSize {
  width: number
  height: number
}

/**
 * useStageSize — reactive stage dimensions for Konva canvases (plan-30 T5).
 *
 * Returns a stable object reference that changes only on window resize —
 * safe in `useMemo`/`useEffect` dep lists. Replaces three ad-hoc patterns:
 * YardMap's duplicated inline listener, SwitchListPreviewMapModal's
 * module-scope constant (stale forever after rotation), and
 * YardMapOverlayModal's per-render recompute (a fresh object every render,
 * which defeated downstream memoization).
 */
export function useStageSize({ widthFactor, heightOffset }: StageSizeOptions): StageSize {
  const [size, setSize] = useState<StageSize>(() => ({
    width: window.innerWidth * widthFactor,
    height: window.innerHeight - heightOffset,
  }))

  useEffect(() => {
    const onResize = () =>
      setSize({
        width: window.innerWidth * widthFactor,
        height: window.innerHeight - heightOffset,
      })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [widthFactor, heightOffset])

  return size
}
